'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { INITIAL_DOTS, getDotColor, formatTimestamp, getCameraLabel } from '@/data/dotsData';
import type { DotPerson, DotColor } from '@/data/dotsData';
import {
  SignalSlashIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  FunnelIcon,
  ArrowPathIcon,
  ClockIcon,
  UserIcon,
  VideoCameraIcon,
  VideoCameraSlashIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import Icon from '@/components/ui/AppIcon';


type EventType = 'disconnection' | 'reconnection' | 'offline';

interface TimelineEvent {
  id: string;
  type: EventType;
  personnelId: string;
  personnelName: string;
  cameras: string;
  timestamp: string;
  acknowledged: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  dotColor: DotColor;
}

function generateEvents(dots: DotPerson[]): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  dots.forEach((dot) => {
    const color = getDotColor(dot.lastUpdate);
    const diffMs = Date.now() - new Date(dot.lastUpdate).getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (color === 'red') {
      // offline event at lastUpdate
      events.push({
        id: `offline-${dot.id}`,
        type: 'offline',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        timestamp: dot.lastUpdate,
        acknowledged: false,
        dotColor: 'red',
      });
      // also a disconnection event ~30 min before lastUpdate
      const disconnectTime = new Date(new Date(dot.lastUpdate).getTime() - 30 * 60 * 1000).toISOString();
      events.push({
        id: `disc-${dot.id}`,
        type: 'disconnection',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        timestamp: disconnectTime,
        acknowledged: diffMin > 90,
        acknowledgedBy: diffMin > 90 ? 'Operador Principal' : undefined,
        acknowledgedAt: diffMin > 90 ? new Date(new Date(disconnectTime).getTime() + 10 * 60 * 1000).toISOString() : undefined,
        dotColor: 'red',
      });
    } else if (color === 'yellow') {
      events.push({
        id: `disc-${dot.id}`,
        type: 'disconnection',
        personnelId: dot.dotId,
        personnelName: dot.name,
        cameras: getCameraLabel(dot.cameras),
        timestamp: dot.lastUpdate,
        acknowledged: false,
        dotColor: 'yellow',
      });
    } else {
      // blue — recently reconnected, show a reconnection event
      if (diffMin > 2) {
        const prevDisconnect = new Date(new Date(dot.lastUpdate).getTime() - 20 * 60 * 1000).toISOString();
        events.push({
          id: `reconn-${dot.id}`,
          type: 'reconnection',
          personnelId: dot.dotId,
          personnelName: dot.name,
          cameras: getCameraLabel(dot.cameras),
          timestamp: dot.lastUpdate,
          acknowledged: true,
          acknowledgedBy: 'Sistema',
          acknowledgedAt: dot.lastUpdate,
          dotColor: 'blue',
        });
      }
    }
  });

  // Sort newest first
  return events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

const EVENT_META: Record<EventType, { label: string; color: string; bg: string; border: string; icon: React.ElementType }> = {
  disconnection: {
    label: 'Desconexión',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    border: 'border-amber-400/30',
    icon: SignalSlashIcon,
  },
  offline: {
    label: 'Fuera de línea',
    color: 'text-red-400',
    bg: 'bg-red-400/10',
    border: 'border-red-400/30',
    icon: ExclamationTriangleIcon,
  },
  reconnection: {
    label: 'Reconexión',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    icon: SignalIcon,
  },
};

const FILTER_OPTIONS: { value: 'all' | EventType; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'disconnection', label: 'Desconexiones' },
  { value: 'offline', label: 'Fuera de línea' },
  { value: 'reconnection', label: 'Reconexiones' },
];

export default function CameraTimelineScreen() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [filter, setFilter] = useState<'all' | EventType>('all');
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [lastRefresh, setLastRefresh] = useState('');
  
  useEffect(() => {
    setEvents(generateEvents(INITIAL_DOTS));
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setLastRefresh(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
  }, []);

  function handleAcknowledge(eventId: string) {
    setEvents((prev) =>
      prev.map((e) =>
        e.id === eventId
          ? {
              ...e,
              acknowledged: true,
              acknowledgedBy: 'Operador Principal',
              acknowledgedAt: new Date().toISOString(),
            }
          : e
      )
    );
  }

  function handleRefresh() {
    setEvents(generateEvents(INITIAL_DOTS));
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setLastRefresh(`${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`);
  }

  const filtered = events.filter((e) => {
    if (filter !== 'all' && e.type !== filter) return false;
    if (showOnlyPending && e.acknowledged) return false;
    return true;
  });

  const pendingCount = events.filter((e) => !e.acknowledged).length;
  const totalDisconnections = events.filter((e) => e.type === 'disconnection').length;
  const totalOffline = events.filter((e) => e.type === 'offline').length;
  const totalReconnections = events.filter((e) => e.type === 'reconnection').length;

  return (
    <div className="flex flex-col h-full bg-background overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
            title="Volver al mapa"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClockIcon className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-foreground">Línea de Tiempo</h1>
            <p className="text-xs text-muted-foreground">Historial de eventos de cámaras</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-muted-foreground hidden sm:block">
              Actualizado: {lastRefresh}
            </span>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Actualizar
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-4 gap-px bg-border shrink-0">
        {[
          { label: 'Pendientes', value: pendingCount, color: 'text-amber-400', bg: 'bg-amber-400/10' },
          { label: 'Desconexiones', value: totalDisconnections, color: 'text-amber-400', bg: 'bg-background' },
          { label: 'Fuera de línea', value: totalOffline, color: 'text-red-400', bg: 'bg-background' },
          { label: 'Reconexiones', value: totalReconnections, color: 'text-emerald-400', bg: 'bg-background' },
        ].map((kpi) => (
          <div key={kpi.label} className={`flex flex-col items-center justify-center py-3 ${kpi.bg}`}>
            <span className={`text-xl font-bold tabular-nums ${kpi.color}`}>{kpi.value}</span>
            <span className="text-xs text-muted-foreground mt-0.5">{kpi.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-border shrink-0 flex-wrap">
        <FunnelIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <div className="flex gap-1 flex-wrap">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                filter === opt.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setShowOnlyPending(!showOnlyPending)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
              showOnlyPending
                ? 'bg-amber-400/20 text-amber-400 border border-amber-400/30' :'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <ExclamationTriangleIcon className="w-3 h-3" />
            Solo pendientes
          </button>
        </div>
      </div>

      {/* Timeline List */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
            <CheckCircleIcon className="w-10 h-10 opacity-30" />
            <p className="text-sm">No hay eventos para mostrar</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-[19px] top-0 bottom-0 w-px bg-border" />

            <div className="flex flex-col gap-0">
              {filtered.map((event, idx) => {
                const meta = EVENT_META[event.type];
                const Icon = meta.icon;
                return (
                  <div key={event.id} className="relative flex gap-4 pb-5 group">
                    {/* Timeline dot */}
                    <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 border ${meta.border} ${meta.bg}`}>
                      <Icon className={`w-4 h-4 ${meta.color}`} />
                    </div>

                    {/* Card */}
                    <div className={`flex-1 rounded-xl border ${meta.border} bg-card p-4 transition-all duration-150 hover:shadow-md`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        {/* Left: event info */}
                        <div className="flex flex-col gap-1.5 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} border ${meta.border}`}>
                              {meta.label}
                            </span>
                            {!event.acknowledged && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/30">
                                Pendiente
                              </span>
                            )}
                            {event.acknowledged && (
                              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/30 flex items-center gap-1">
                                <CheckCircleSolid className="w-3 h-3" />
                                Confirmado
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4 flex-wrap mt-0.5">
                            {/* Personnel */}
                            <div className="flex items-center gap-1.5 text-sm">
                              <UserIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="font-medium text-foreground">{event.personnelName}</span>
                              <span className="text-muted-foreground text-xs">#{event.personnelId}</span>
                            </div>
                            {/* Camera */}
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              {event.type === 'reconnection' ? (
                                <VideoCameraIcon className="w-3.5 h-3.5 shrink-0" />
                              ) : (
                                <VideoCameraSlashIcon className="w-3.5 h-3.5 shrink-0" />
                              )}
                              <span>{event.cameras}</span>
                            </div>
                          </div>

                          {/* Timestamp */}
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <ClockIcon className="w-3 h-3 shrink-0" />
                            <span>{formatTimestamp(event.timestamp)}</span>
                          </div>

                          {/* Acknowledged info */}
                          {event.acknowledged && event.acknowledgedBy && event.acknowledgedAt && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                              <CheckCircleIcon className="w-3 h-3 shrink-0 text-emerald-400" />
                              <span>
                                Confirmado por <span className="text-foreground font-medium">{event.acknowledgedBy}</span>
                                {' · '}{formatTimestamp(event.acknowledgedAt)}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Right: acknowledge button */}
                        {!event.acknowledged && (
                          <button
                            onClick={() => handleAcknowledge(event.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium hover:bg-primary/20 transition-all duration-150 shrink-0 mt-0.5"
                          >
                            <CheckCircleIcon className="w-3.5 h-3.5" />
                            Confirmar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
